import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Eye,
  EyeSlash as EyeOff,
  Key as KeyRound,
  Lightning as Zap,
  Lock,
  Shield,
  ShieldCheck,
  SpinnerGap as Loader2,
  User,
  WarningCircle as AlertCircle,
  ArrowRight
} from '@phosphor-icons/react';
import { parseApiError } from '@/lib/utils';
import apiClient from '@/lib/axiosInstance';
import { setUserRole } from '@/lib/userRole';
import { useLanguage } from '@/contexts/LanguageContext';
import { storeTokens } from '@/lib/session';

const Login = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [checkingSetup, setCheckingSetup] = useState(true);

  // MFA states
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaToken, setMfaToken] = useState('');
  const [mfaCode, setMfaCode] = useState('');

  useEffect(() => {
    const checkSetupStatus = async () => {
      try {
        const response = await apiClient.get('/auth/setup-status', { timeout: 5000 });
        const data = response.data;
        if (data.setup_required === true && data.message) {
          navigate('/setup');
          return;
        }
      } catch (err) {
        // silencio-ok: si no se sabe si falta el setup se enseña el login, que ya avisa si la API no responde
        console.warn('Setup check failed, showing login:', err.message);
      } finally {
        setCheckingSetup(false);
      }
    };
    checkSetupStatus();
  }, [navigate]);

  const completeLogin = (data) => {
    // Un solo sitio guarda los tokens (FE-11): lib/session.
    storeTokens({ accessToken: data.access_token, refreshToken: data.refresh_token });
    localStorage.setItem('user', JSON.stringify(data.user));
    sessionStorage.setItem('isAuthenticated', 'true');
    sessionStorage.setItem('userEmail', data.user.email);
    // El rol va por el almacen compartido (que ademas escribe sessionStorage):
    // asi las pantallas ya montadas se enteran en el acto. La rehidratacion en
    // pestaña nueva la hace AuthContext desde GET /api/auth/me.
    setUserRole(data.user.role);

    if (data.user.must_change_password) {
      // Forzar cambio de contraseña antes de usar la app (cuenta nueva o
      // password reseteado por un admin). ProtectedRoute bloquea el resto de
      // rutas mientras este flag siga en sessionStorage.
      sessionStorage.setItem('mustChangePassword', 'true');
      navigate('/change-password');
      return;
    }

    navigate('/tenant-selection');
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const formData = new URLSearchParams();
      formData.append('username', email);
      formData.append('password', password);

      const response = await apiClient.post('/auth/login', formData, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
      });

      const data = response.data;

      if (data.mfa_required) {
        setMfaRequired(true);
        setMfaToken(data.mfa_token);
        setLoading(false);
        return;
      }

      completeLogin(data);
    } catch (err) {
      setError(parseApiError(err.response?.data?.detail, t('auth.invalidCredentials')) || t('auth.invalidCredentials'));
    } finally {
      setLoading(false);
    }
  };

  const handleMfaSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await apiClient.post('/auth/login/mfa', {
        mfa_token: mfaToken,
        mfa_code: mfaCode
      });

      completeLogin(response.data);
    } catch (err) {
      setError(parseApiError(err.response?.data?.detail, t('auth.invalidMfaCode')) || t('auth.invalidMfaCode'));
    } finally {
      setLoading(false);
    }
  };

  const resetMfa = () => {
    setMfaRequired(false);
    setMfaToken('');
    setMfaCode('');
    setError('');
  };

  if (checkingSetup) {
    return (
      <div className="min-h-screen bg-[#0D1117] flex items-center justify-center p-6 font-sans">
        <div className="text-center">
          <div className="relative w-12 h-12 mx-auto">
            <div className="w-12 h-12 border border-cyan-500/15 rounded-full animate-spin border-t-cyan-400"></div>
          </div>
          <p className="text-slate-500 mt-4 text-xs font-mono uppercase tracking-[0.2em]">{t('common.initializing')}</p>
        </div>
      </div>
    );
  }

  const FEATURES = ['Attack Paths', 'AD Security', 'MITRE ATT&CK', 'Real-time'];
  const TRUST = ['SOC 2', 'ISO 27001', 'GDPR', 'MITRE ATT&CK'];

  return (
    <div className="min-h-screen bg-[#0D1117] flex font-sans antialiased text-slate-100">
      {/* ═══════════════════════════════════════
          LEFT HERO — minimal Onyx, hairline, restrained
          ═══════════════════════════════════════ */}
      <div className="hidden lg:flex lg:w-1/2 xl:w-3/5 relative overflow-hidden border-r border-cyan-500/[0.08]">
        {/* Subtle grid pattern */}
        <div
          className="absolute inset-0 opacity-[0.5]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(34,211,238,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(34,211,238,0.04) 1px, transparent 1px)',
            backgroundSize: '32px 32px'
          }}
        ></div>
        {/* One restrained glow, NOT 3 ostentatious orbs */}
        <div className="absolute w-[640px] h-[640px] bg-cyan-500/[0.05] rounded-full blur-[140px] -top-40 -left-40"></div>
        <div className="absolute w-[400px] h-[400px] bg-violet-500/[0.04] rounded-full blur-[120px] bottom-0 right-0"></div>

        <div className="relative z-10 flex flex-col justify-center px-12 xl:px-20 max-w-2xl w-full">
          {/* Wordmark logo */}
          <div className="flex items-center gap-3 mb-16">
            <div className="w-2 h-2 rounded-full bg-cyan-400 shadow-[0_0_10px_rgba(34,211,238,0.7)]"></div>
            <span className="text-xl font-semibold text-white tracking-tight">VEKTOR</span>
            <span className="text-[10px] font-mono text-cyan-400/80 uppercase tracking-[0.22em]">CTEM Platform</span>
          </div>

          {/* Eyebrow */}
          <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-cyan-400/90 mb-5">
            // Continuous Threat Exposure Management
          </p>

          {/* Headline restrained */}
          <h2 className="text-3xl xl:text-5xl font-semibold text-white leading-[1.05] tracking-tight mb-6">
            Enterprise breach<br />
            <span className="text-slate-300">&amp; attack simulation.</span>
          </h2>

          <p className="text-slate-400 text-base leading-relaxed mb-12 max-w-md">
            Continuous validation. Real attack paths. Zero noise.
          </p>

          {/* Feature pills inline */}
          <div className="flex flex-wrap gap-2 mb-14">
            {FEATURES.map((label) => (
              <span
                key={label}
                className="px-3 py-1.5 rounded-md text-[11px] font-mono uppercase tracking-wider bg-cyan-500/[0.06] border border-cyan-500/[0.15] text-cyan-300"
              >
                {label}
              </span>
            ))}
          </div>

          {/* Trust badges */}
          <div className="flex items-center gap-4 text-[10px] font-mono text-slate-500 uppercase tracking-[0.18em]">
            {TRUST.map((label, i) => (
              <React.Fragment key={label}>
                {i > 0 && <span className="w-px h-3 bg-slate-700"></span>}
                <span>{label}</span>
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      {/* ═══════════════════════════════════════
          RIGHT FORM — hairline glass card
          ═══════════════════════════════════════ */}
      <div className="w-full lg:w-1/2 xl:w-2/5 flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center justify-center gap-3 mb-10">
            <div className="w-2 h-2 rounded-full bg-cyan-400 shadow-[0_0_10px_rgba(34,211,238,0.7)]"></div>
            <span className="text-xl font-semibold text-white tracking-tight">VEKTOR</span>
            <span className="text-[10px] font-mono text-cyan-400/80 uppercase tracking-[0.22em]">CTEM</span>
          </div>

          <Card className="bg-slate-900/40 border border-cyan-500/[0.12] backdrop-blur-xl rounded-xl shadow-[0_24px_64px_-16px_rgba(0,0,0,0.65),0_8px_24px_-8px_rgba(0,0,0,0.4)]">
            <CardContent className="p-7">
              {!mfaRequired ? (
                <>
                  <div className="mb-8">
                    <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-cyan-400/90 mb-2">
                      // Authenticate
                    </p>
                    <h2 className="text-2xl font-semibold text-white tracking-tight mb-1">
                      {t('auth.welcomeBack')}
                    </h2>
                    <p className="text-slate-500 text-sm">{t('auth.signInSubtitle')}</p>
                  </div>

                  <form onSubmit={handleLogin} className="space-y-5">
                    {/* Email */}
                    <div>
                      <label className="block text-[11px] font-mono uppercase tracking-wider text-slate-500 mb-2">
                        {t('auth.emailAddress')}
                      </label>
                      <div className="relative group">
                        <User className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-600 group-focus-within:text-cyan-400 transition-colors" />
                        <input
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="analyst@company.com"
                          className="w-full pl-10 pr-4 py-2.5 bg-slate-950/60 border border-white/[0.08] rounded-md text-sm text-white placeholder-slate-600 focus:border-cyan-400/40 focus:outline-none focus:ring-1 focus:ring-cyan-400/20 transition-all"
                          required
                        />
                      </div>
                    </div>

                    {/* Password */}
                    <div>
                      <label className="block text-[11px] font-mono uppercase tracking-wider text-slate-500 mb-2">
                        {t('auth.password')}
                      </label>
                      <div className="relative group">
                        <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-600 group-focus-within:text-cyan-400 transition-colors" />
                        <input
                          type={showPassword ? 'text' : 'password'}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder={t('auth.enterPassword')}
                          className="w-full pl-10 pr-10 py-2.5 bg-slate-950/60 border border-white/[0.08] rounded-md text-sm text-white placeholder-slate-600 focus:border-cyan-400/40 focus:outline-none focus:ring-1 focus:ring-cyan-400/20 transition-all"
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-600 hover:text-slate-300 transition-colors"
                        >
                          {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>

                    {/* Error */}
                    {error && (
                      <div className="flex items-center gap-3 p-3 bg-red-500/[0.08] border border-red-500/[0.25] rounded-md">
                        <AlertCircle className="h-4 w-4 text-red-400 flex-shrink-0" />
                        <span className="text-xs text-red-300">{error}</span>
                      </div>
                    )}

                    {/* Submit — cyan solid restrained, no gradient */}
                    <Button
                      type="submit"
                      disabled={loading}
                      className="w-full h-11 bg-cyan-400 hover:bg-cyan-300 text-slate-950 font-medium text-sm rounded-md shadow-[0_0_0_1px_rgba(34,211,238,0.3),0_8px_20px_-8px_rgba(34,211,238,0.4)] hover:shadow-[0_0_0_1px_rgba(34,211,238,0.5),0_10px_24px_-8px_rgba(34,211,238,0.6)] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? (
                        <span className="flex items-center justify-center gap-2">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          {t('auth.signingIn')}
                        </span>
                      ) : (
                        <span className="flex items-center justify-center gap-2">
                          {t('auth.signIn')}
                          <ArrowRight className="h-4 w-4" />
                        </span>
                      )}
                    </Button>

                    {/* Secondary actions — match mockup */}
                    <div className="flex items-center justify-between text-[11px] font-mono uppercase tracking-wider text-slate-500 pt-1">
                      <button type="button" className="hover:text-cyan-400 transition-colors">{t('auth.ssoWithSaml')}</button>
                      <button type="button" className="hover:text-cyan-400 transition-colors">{t('auth.forgotPassword')}</button>
                    </div>
                  </form>
                </>
              ) : (
                <>
                  <div className="mb-8">
                    <div className="w-10 h-10 rounded-md bg-cyan-500/[0.1] border border-cyan-500/[0.2] flex items-center justify-center mb-4">
                      <KeyRound className="h-5 w-5 text-cyan-400" />
                    </div>
                    <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-cyan-400/90 mb-2">
                      // Two-factor
                    </p>
                    <h2 className="text-2xl font-semibold text-white tracking-tight mb-1">
                      {t('auth.mfaTitle')}
                    </h2>
                    <p className="text-slate-500 text-sm">{t('auth.mfaSubtitle')}</p>
                  </div>

                  <form onSubmit={handleMfaSubmit} className="space-y-5">
                    <div>
                      <label className="block text-[11px] font-mono uppercase tracking-wider text-slate-500 mb-2">
                        {t('auth.verificationCode')}
                      </label>
                      <input
                        type="text"
                        value={mfaCode}
                        onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        placeholder="000000"
                        className="w-full py-4 bg-slate-950/60 border border-white/[0.08] rounded-md text-white placeholder-slate-700 focus:border-cyan-400/40 focus:outline-none focus:ring-1 focus:ring-cyan-400/20 transition-all text-center text-2xl tracking-[0.5em] font-mono"
                        maxLength={6}
                        autoFocus
                        required
                      />
                      <p className="text-[11px] font-mono text-slate-500 mt-2 text-center uppercase tracking-wider">
                        {t('auth.openAuthApp')}
                      </p>
                    </div>

                    {error && (
                      <div className="flex items-center gap-3 p-3 bg-red-500/[0.08] border border-red-500/[0.25] rounded-md">
                        <AlertCircle className="h-4 w-4 text-red-400 flex-shrink-0" />
                        <span className="text-xs text-red-300">{error}</span>
                      </div>
                    )}

                    <Button
                      type="submit"
                      disabled={loading || mfaCode.length !== 6}
                      className="w-full h-11 bg-cyan-400 hover:bg-cyan-300 text-slate-950 font-medium text-sm rounded-md shadow-[0_0_0_1px_rgba(34,211,238,0.3),0_8px_20px_-8px_rgba(34,211,238,0.4)] hover:shadow-[0_0_0_1px_rgba(34,211,238,0.5),0_10px_24px_-8px_rgba(34,211,238,0.6)] transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      {loading ? (
                        <span className="flex items-center justify-center gap-2">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          {t('auth.verifying')}
                        </span>
                      ) : (
                        <span className="flex items-center justify-center gap-2">
                          {t('auth.verifyAndSignIn')}
                          <ArrowRight className="h-4 w-4" />
                        </span>
                      )}
                    </Button>

                    <Button
                      type="button"
                      onClick={resetMfa}
                      variant="ghost"
                      className="w-full text-slate-400 hover:text-white hover:bg-white/[0.04] py-2 text-sm"
                    >
                      {t('auth.useDifferentAccount')}
                    </Button>
                  </form>
                </>
              )}

              {/* Footer trust signals */}
              <div className="mt-8 pt-6 border-t border-white/[0.06]">
                <div className="flex items-center justify-center gap-5 text-[10px] font-mono uppercase tracking-[0.18em] text-slate-600">
                  <span className="flex items-center gap-1.5">
                    <Lock className="h-3 w-3" />
                    {t('auth.footer.aes256')}
                  </span>
                  <span className="w-px h-3 bg-slate-800"></span>
                  <span className="flex items-center gap-1.5">
                    <ShieldCheck className="h-3 w-3" />
                    {t('auth.footer.soc2')}
                  </span>
                  <span className="w-px h-3 bg-slate-800"></span>
                  <span className="flex items-center gap-1.5">
                    <Shield className="h-3 w-3" />
                    {t('auth.footer.gdpr')}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          <p className="text-center text-slate-700 text-[10px] mt-6 font-mono uppercase tracking-[0.22em]">
            VEKTOR · v2.3.0 · {t('auth.enterpriseSecurity')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
